import { TableProps } from '@/components/table'
import { Query } from './collection_query'
import { AttachmentsQuery } from './__generated__/collection_query'
import { DocumentNode, useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { AttachmentFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { Heading } from '../base/heading'
import { Text } from '../base/text'
import { DeleteDialog } from '../delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useInfiniteScroll } from '@/helpers/useInfiniteScroll'
import { fileExtension, imageExtensions } from '@/helpers/file_extension'
import { DocumentIcon } from '@heroicons/react/24/solid'

interface Props extends TableProps {
  filters: AttachmentFilters
  createable?: boolean
  deleteable?: boolean
  refetchQueries?: DocumentNode[]
}

export const Collection = ({
  pageSize = 10,
  filters,
  createable = false,
  deleteable = false,
  refetchQueries = [],
  ...props
}: Props) => {
  const { data, loading, error, fetchMore } = useQuery<AttachmentsQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openDelete,
    setOpenDelete
  } = useTable({ pageInfo: data?.attachments.pageInfo })
  const { setLoaderElement } = useInfiniteScroll({
    fetchMore: () => fetchMore({
      variables: { after: pageInfo.current?.endCursor },
      updateQuery(previousData, { fetchMoreResult }) {
        if (Object.keys(previousData).length === 0) return fetchMoreResult
        fetchMoreResult.attachments.nodes = dedupe([
          ...previousData.attachments.nodes,
          ...fetchMoreResult.attachments.nodes
        ])
        return { ...fetchMoreResult }
      }
    })
  })
  const [attachmentId, setAttachmentId] = useState<string>()

  const deleteAttachment = (id: string) => {
    setAttachmentId(id)
    setOpenDelete(true)
  }

  const onDeleteSuccess = () => {
    setAttachmentId(undefined)
    setOpenDelete(false)
  }

  if (error) return <Error message={error.message} />
  if (loading || !data) return <Loading />

  return (
    <>
      <div className={`flex flex-col gap-2 ${props.fixedHeight ? 'h-96' : ''}`}>
        <div className="flex flex-row justify-between">
          { props.title && <Heading className="text-nowrap">{ props.title }</Heading> }
          <span className="w-full flex justify-end">
            {
              createable
                ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button>
                : undefined
            }
          </span>
        </div>
        <div className={`h-full ${props.fixedHeight ? 'max-h-96 overflow-y-auto' : ''}`}>
          <div className="flex flex-wrap justify-around gap-6">
            { data.attachments.nodes.length == 0 && (
              <div className="flex p-4 items-center justify-center w-full">
                <div className="w-full flex flex-row justify-center items-center gap-6">
                  <Text>Nothing to see here... yet</Text>
                </div>
              </div>
            )}
            { data.attachments.nodes.map(attachment => (
              <div
                key={attachment.id}
                className={`
                  flex flex-row rounded-md bg-zinc-200 dark:bg-zinc-800 border border-white/10 p-2 items-center
                  justify-around relative
                `}
              >
                { deleteable && <XMarkIcon
                  className="size-5 absolute right-1 top-1"
                  color="lightgray"
                  onClick={() => deleteAttachment(attachment.id)}
                /> }
                { imageExtensions.includes(fileExtension(attachment.filename))
                  ? <img className="object-contain size-48" src={attachment.url} />
                  : <div className="flex flex-col items-center">
                    <DocumentIcon className="w-36 h-36" />
                    <a className="text-gray-500" href={attachment.url} target="_blank" download>{ attachment.filename }</a>
                  </div> }
              </div>
            ))}
            {
              pageInfo.current?.hasNextPage
                ? <div className="flex p-4 items-center justify-center w-full">
                  <div ref={setLoaderElement} className="flex p-4 items-center justify-center">
                    <Loading />
                  </div>
                </div>
                : data.attachments.nodes.length == 0
                  ? <></>
                  : <div className="flex p-4 items-center justify-center w-full">
                    <div className="w-full flex flex-row justify-center items-center gap-6">
                      <Text>That's all we've got for now</Text>
                    </div>
                  </div>
            }
          </div>
        </div>
      </div>
      { attachmentId && <DeleteDialog
        id={attachmentId}
        mutation={DeleteMutation}
        open={openDelete}
        refetchQueries={[...refetchQueries, Query]}
        setOpen={setOpenDelete}
        onSuccess={onDeleteSuccess}
      /> }
      <NewDialog
        defaultValues={{ key: filters.nameEq, resourceId: filters.resourceIdEq, resourceType: filters.resourceTypeEq }}
        open={openNew}
        refetchQueries={refetchQueries}
        setOpen={setOpenNew}
      />
    </>
  )
}
